// Módulo 5: historial de cambios y aprobaciones, quién pidió y quién aprobó.
import { Link } from "@tanstack/react-router";
import { EsqueletoTabla, Pill, TituloPagina, Vacio } from "@iavmx/ui";
import { useConSesion, useHistorial, type Historial as DatosHistorial } from "./consultas";
import { EncabezadoSeccion } from "./componentes";
import { etiquetaCampo, fechaHora, valorLegible } from "./util";

type Evento = {
  id: string;
  campo: string;
  valorAnterior: unknown;
  valorNuevo: unknown;
  solicitadoPorNombre: string | null;
  aprobadoPorNombre: string | null;
  creadoEn: string | Date;
  sobre?: string | { nombre: string; campanaId: string | null; solicitudId: string | null } | null;
};

function textoEvento(e: Evento): string {
  const campo = etiquetaCampo(e.campo);
  if (e.campo === "creada" || e.campo === "creado") return `${campo}: ${valorLegible(e.valorNuevo)}`;
  if (e.valorAnterior == null || e.valorAnterior === "") return `${campo}: ${valorLegible(e.valorNuevo)}`;
  return `${campo} cambió de «${valorLegible(e.valorAnterior)}» a «${valorLegible(e.valorNuevo)}»`;
}

function quien(e: Evento): string {
  if (!e.solicitadoPorNombre && !e.aprobadoPorNombre) return "Automático";
  if (e.solicitadoPorNombre && e.aprobadoPorNombre && e.solicitadoPorNombre === e.aprobadoPorNombre) return `${e.aprobadoPorNombre}`;
  const partes = [];
  if (e.solicitadoPorNombre) partes.push(`Pidió ${e.solicitadoPorNombre}`);
  partes.push(e.aprobadoPorNombre ? `aprobó ${e.aprobadoPorNombre}` : "pendiente de aprobación");
  return partes.join(" · ");
}

/** Lista de eventos reutilizable (ficha, solicitud, historial general). */
export function HistorialLista({ eventos, hoy, conLigas }: { eventos: Evento[]; hoy?: string; conLigas?: boolean }) {
  if (eventos.length === 0) return <p className="font-ui text-sm text-tinta-tenue">Sin cambios registrados.</p>;
  return (
    <ol className="divide-y divide-linea border-y border-linea">
      {eventos.map((e) => {
        const sobre = typeof e.sobre === "string" ? { nombre: e.sobre, campanaId: null, solicitudId: null } : e.sobre;
        return (
          <li key={e.id} className="grid gap-1 py-3 sm:grid-cols-[130px_1fr] sm:gap-4">
            <p className="font-ui text-xs text-tinta-tenue sm:text-sm">{fechaHora(e.creadoEn, hoy)}</p>
            <div className="min-w-0">
              {sobre && (
                <p className="font-ui text-xs font-semibold uppercase tracking-wide text-tinta-tenue">
                  {conLigas && sobre.campanaId ? (
                    <Link to="/campanas/$id" params={{ id: sobre.campanaId }} className="text-azul hover:underline">
                      {sobre.nombre}
                    </Link>
                  ) : conLigas && sobre.solicitudId ? (
                    <Link to="/campanas/solicitudes/$id" params={{ id: sobre.solicitudId }} className="text-azul hover:underline">
                      {sobre.nombre}
                    </Link>
                  ) : (
                    sobre.nombre
                  )}
                </p>
              )}
              <p className="font-ui text-sm text-tinta">{textoEvento(e)}</p>
              <p className="font-ui text-xs text-tinta-tenue">{quien(e)}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function Historial() {
  const conSesion = useConSesion();
  const q = useHistorial();
  if (!conSesion) return <Vacio titulo="Entra para ver el historial" />;
  if (q.isLoading) return <EsqueletoTabla columnas={2} />;
  if (q.error || !q.data) return <Vacio titulo="No se pudo cargar" texto={q.error?.message} />;
  const d: DatosHistorial = q.data;
  return (
    <>
      <TituloPagina descripcion="Quién pidió cada cambio a un texto, diseño, precio o fecha, y quién lo aprobó. Dirección lo ve en modo consulta.">Historial de cambios y aprobaciones</TituloPagina>
      {d.aprobacionesPendientes.length > 0 && (
        <section className="mb-8">
          <EncabezadoSeccion>Aprobaciones pendientes</EncabezadoSeccion>
          <ul className="divide-y divide-linea border-y border-linea">
            {d.aprobacionesPendientes.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-2 py-3 font-ui text-sm">
                <Pill tono="ambar">{a.objeto}</Pill>
                {a.campanaId ? (
                  <Link to="/campanas/$id" params={{ id: a.campanaId }} className="font-semibold text-azul hover:underline">
                    {a.sobre ?? "Campaña"}
                  </Link>
                ) : (
                  <span className="font-semibold">{a.sobre ?? "—"}</span>
                )}
                <span className="text-tinta-tenue">
                  pidió {a.solicitadaPorNombre ?? "alguien"} · valida {a.areaValidadora ?? "Mercadotecnia"} · {fechaHora(a.creadoEn)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section>
        <EncabezadoSeccion>Últimos cambios</EncabezadoSeccion>
        <HistorialLista eventos={d.eventos} conLigas />
      </section>
    </>
  );
}
